import React, { useState, useEffect, useCallback } from "react";
import "./Portfolios.css";
import ContactSection from "../components/Contact/contact_homepage";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://threegor-1.onrender.com";

// API Functions
const fetchPortfolios = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/portfolios`);
    if (!response.ok) {
      throw new Error('Failed to fetch portfolios');
    }
    const data = await response.json();
    return Array.isArray(data) ? data : (data.data || []);
  } catch (error) {
    console.error('Error fetching portfolios:', error);
    throw error;
  }
};

const fetchPortfoliosByCategory = async (category) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/portfolios/category/${category}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch portfolios for category: ${category}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : (data.data || []);
  } catch (error) {
    console.error('Error fetching portfolios by category:', error);
    throw error;
  }
};

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentPortfolioImages, setCurrentPortfolioImages] = useState([]);

  const categories = [
    { id: "all", name: "ทั้งหมด" },
    { id: "kitchen", name: "ชุดครัว" },
    { id: "tv-wall", name: "ผนังทีวี" },
    { id: "closet", name: "ตู้เสื้อผ้า" }
  ];

  // Image Error Handler
  const handleImageError = useCallback((e) => {
    e.target.onError = null;
    e.target.src = "https://via.placeholder.com/600x400?text=Image+Not+Found";
    e.target.style.filter = "grayscale(100%)";
  }, []);

  useEffect(() => {
    loadPortfolios();
  }, [selectedCategory]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!lightboxOpen) return;
      
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        navigateLightbox('prev');
      } else if (e.key === 'ArrowRight') {
        navigateLightbox('next');
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [lightboxOpen, currentImageIndex, currentPortfolioImages]);

  const loadPortfolios = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = selectedCategory === "all" 
        ? await fetchPortfolios()
        : await fetchPortfoliosByCategory(selectedCategory);
      
      setPortfolios(data || []);
    } catch (error) {
      console.error("Error loading portfolios:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Open image in lightbox with navigation
  const openLightbox = (imageUrl, portfolioImages = []) => {
    const allImages = portfolioImages.length > 0 ? portfolioImages : [imageUrl];
    const imageIndex = allImages.findIndex(img => img === imageUrl);
    
    setCurrentPortfolioImages(allImages);
    setCurrentImageIndex(imageIndex >= 0 ? imageIndex : 0);
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // Navigate lightbox images
  const navigateLightbox = (direction) => {
    if (currentPortfolioImages.length <= 1) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentImageIndex + 1) % currentPortfolioImages.length;
    } else {
      newIndex = currentImageIndex === 0 ? currentPortfolioImages.length - 1 : currentImageIndex - 1;
    }
    
    setCurrentImageIndex(newIndex);
    setLightboxImage(currentPortfolioImages[newIndex]);
  };

  // Close lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage("");
    setCurrentPortfolioImages([]);
    setCurrentImageIndex(0);
    document.body.style.overflow = 'auto';
  };

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    if (categoryId !== selectedCategory) {
      setSelectedCategory(categoryId);
    }
  };

  if (loading) {
    return (
      <div className="portfolio-page">
        <div className="portfolio-header">
          <h1>ผลงานของเรา</h1>
          <p>ชมผลงานการออกแบบและก่อสร้างที่โดดเด่นของเรา</p>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <h2>กำลังโหลดผลงาน...</h2>
          <p>โปรดรอสักครู่</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portfolio-page">
        <div className="portfolio-header">
          <h1>ผลงานของเรา</h1>
          <p>ชมผลงานการออกแบบและก่อสร้างที่โดดเด่นของเรา</p>
        </div>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>เกิดข้อผิดพลาด</h2>
          <p>{error}</p>
          <button onClick={loadPortfolios} className="retry-btn">
            ลองอีกครั้ง
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="portfolio-page">
      {/* Header Section */}
      <div className="portfolio-header">
        <h1>ผลงานของเรา</h1>
      </div>

      {/* Filter Section */}
      <div className="portfolio-filter">
        {categories.map((category) => (
          <button
            key={category.id}
            className={`filter-btn ${selectedCategory === category.id ? 'active' : ''}`}
            onClick={() => handleCategoryChange(category.id)}
            aria-pressed={selectedCategory === category.id}
          >
            <span>{category.name}</span>
          </button>
        ))}
      </div>

      {/* Portfolio Count */}
      <div className="portfolio-count">
        <p>แสดง {portfolios.length} โครงการ</p>
      </div>

      {/* Portfolio Grid */}
      {portfolios.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📂</div>
          <h2>ไม่พบผลงาน</h2>
          <p>ไม่พบผลงานในหมวดหมู่นี้ในขณะนี้</p>
        </div>
      ) : (
        <div className="portfolio-grid">
          {portfolios.map((portfolio) => {
            // Get up to 4 images for the 2x2 grid
            const displayImages = portfolio.images ? portfolio.images.slice(0, 4) : [];
            const allImages = portfolio.images || [];
            
            return (
              <article key={portfolio._id || portfolio.id} className="portfolio-card">
                {/* 2x2 Image Grid */}
                <div className="portfolio-image-grid">
                  {displayImages.length > 0 ? (
                    displayImages.map((img, idx) => (
                      <div 
                        key={idx} 
                        className={`grid-image grid-image-${idx + 1}`}
                        onClick={() => openLightbox(img, allImages)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openLightbox(img, allImages);
                          }
                        }}
                        aria-label={`ดูรูปภาพ ${idx + 1} ของ ${portfolio.title}`}
                      >
                        <img 
                          src={img} 
                          alt={`${portfolio.title} - รูปที่ ${idx + 1}`} 
                          onError={handleImageError}
                          loading="lazy"
                        />
                        <div className="image-overlay">
                          <span className="zoom-icon">🔍</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="grid-image no-image">
                      <img 
                        src="https://via.placeholder.com/600x400?text=No+Images" 
                        alt="ไม่มีรูปภาพ"
                        loading="lazy"
                      />
                    </div>
                  )}
                  
                  {/* Fill empty slots if less than 4 images */}
                  {displayImages.length < 4 && Array.from({ length: 4 - displayImages.length }).map((_, idx) => (
                    <div key={`empty-${idx}`} className={`grid-image grid-image-${displayImages.length + idx + 1} empty-slot`}>
                      <div className="placeholder-content">
                        <span>📷</span>
                      </div>
                    </div>
                  ))}
                  
                  {/* Image Count Badge */}
                  {allImages.length > 4 && (
                    <div className="image-count-badge">
                      +{allImages.length - 4} รูป
                    </div>
                  )}
                </div>
                
                {/* Portfolio Content */}
                <div className="portfolio-content">
                  <h3 className="portfolio-title">{portfolio.title}</h3>
                  
                  <div className="portfolio-meta">
                    <div className="meta-item">
                      <span className="meta-icon">📍</span>
                      <span className="meta-text">{portfolio.location || 'ไม่ระบุสถานที่'}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-icon">📐</span>
                      <span className="meta-text">{portfolio.area || 'ไม่ระบุขนาด'}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-icon">📅</span>
                      <span className="meta-text">{portfolio.year || 'ไม่ระบุปี'}</span>
                    </div>
                  </div>

                  <p className="portfolio-description">
                    {portfolio.description || 'ไม่มีรายละเอียด'}
                  </p>

                  {/* Features Tags */}
                  {portfolio.features && portfolio.features.length > 0 && (
                    <div className="portfolio-tags">
                      {portfolio.features.slice(0, 3).map((feature, idx) => (
                        <span key={idx} className="tag">{feature}</span>
                      ))}
                      {portfolio.features.length > 3 && (
                        <span className="tag more-tag">+{portfolio.features.length - 3}</span>
                      )}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Enhanced Lightbox with Navigation */}
      {lightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <button 
            className="lightbox-close" 
            onClick={closeLightbox}
            aria-label="ปิด"
          >
            ✕
          </button>
          
          {/* Navigation Arrows */}
          {currentPortfolioImages.length > 1 && (
            <>
              <button 
                className="lightbox-nav lightbox-prev" 
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox('prev');
                }}
                aria-label="รูปก่อนหน้า"
              >
                ‹
              </button>
              <button 
                className="lightbox-nav lightbox-next" 
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox('next');
                }}
                aria-label="รูปถัดไป"
              >
                ›
              </button>
            </>
          )}
          
          {/* Image Counter */}
          {currentPortfolioImages.length > 1 && (
            <div className="lightbox-counter">
              {currentImageIndex + 1} / {currentPortfolioImages.length}
            </div>
          )}
          
          <img 
            src={lightboxImage} 
            alt="ภาพขยาย" 
            className="lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* CTA Section */}
      <div className="portfolio-cta">
        <h2>สนใจให้เราออกแบบให้คุณบ้าง?</h2>
        <p>ปรึกษาฟรี! เริ่มต้นสร้างฝันให้เป็นจริงไปกับเรา</p>
        <button 
          className="cta-button"
          onClick={() => window.open('https://www.facebook.com/messages/t/100064243770130', '_blank')}
          aria-label="ปรึกษาโครงการผ่าน Facebook"
        >
          ปรึกษาโครงการ
        </button>
      </div>

      <ContactSection />
    </div>
  );
};

export default Portfolio;